// My CRM — shared bits: data hook, chips, table cells.
import { useCallback, useEffect, useState } from "react";
import api from "../../../lib/api";

export const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
export const dshort = (s) => (s ? new Date(String(s).length <= 10 ? `${s}T12:00:00` : s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" }) : "—");
export const ago = (s) => {
  if (!s) return "—";
  const d = Math.round((Date.now() - new Date(s).getTime()) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : d < 60 ? `${d}d ago` : dshort(s);
};
export const th = "px-3 py-2 text-left text-[10px] font-mono uppercase tracking-widest text-slate-400 whitespace-nowrap";
export const td = "px-3 py-2 text-[12.5px] text-slate-200 align-top";

export function useMyCrm(path, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params);
  const load = useCallback(() => {
    setLoading(true);
    const p = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v != null));
    return api.get(`/my-crm/${path}`, { params: p }).then(({ data: d }) => setData(d)).catch(() => setData({ rows: [], total: 0, facets: {} })).finally(() => setLoading(false));
  }, [path, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export function Chips({ items, value, onChange, testid, single }) {
  const set = new Set(value);
  const toggle = (k) => {
    if (single) return onChange(set.has(k) ? [] : [k]);
    const n = new Set(set); n.has(k) ? n.delete(k) : n.add(k); onChange([...n]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {items.map(([k, label, n, tone]) => (
        <button key={k} type="button" onClick={() => toggle(k)} data-testid={`${testid}-${k}`}
          className={`px-2.5 py-1 rounded-full text-[11.5px] border transition-colors ${set.has(k) ? (tone || "bg-amber-500/15 border-amber-400/50 text-amber-100") : "border-white/10 text-slate-300 hover:border-white/25"}`}>
          {label}{n != null && <span className="ml-1.5 font-mono text-[10px] opacity-70">{n}</span>}
        </button>
      ))}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, testid }) {
  return <input className="ws-input !rounded-full w-full sm:w-72" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />;
}

export function SortSelect({ value, onChange, options, testid }) {
  return (
    <select className="ws-input !w-auto" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map(([k, l]) => <option key={k} value={k}>Sort: {l}</option>)}
    </select>
  );
}

export function useDebounced(v, ms = 300) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

export function Pager({ page, total, size = 50, onPage }) {
  const pages = Math.max(1, Math.ceil(total / size));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 px-3 py-2 text-[11.5px] text-slate-400" data-testid="mycrm-pager">
      <button className="px-2 py-1 rounded border border-white/10 disabled:opacity-40" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="mycrm-prev">Prev</button>
      <span>Page {page} of {pages}</span>
      <button className="px-2 py-1 rounded border border-white/10 disabled:opacity-40" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="mycrm-next">Next</button>
    </div>
  );
}

export const Empty = ({ text }) => <div className="px-4 py-10 text-center text-[13px] text-slate-500" data-testid="mycrm-empty">{text}</div>;

export const TIER_TONE = { BLACK: "text-slate-100 bg-slate-500/25", PLATINUM: "text-cyan-200 bg-cyan-500/15", GOLD: "text-amber-200 bg-amber-500/15", SILVER: "text-slate-200 bg-slate-400/15", BRONZE: "text-orange-200 bg-orange-500/15" };
export const Tier = ({ t }) => t ? <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${TIER_TONE[t] || "bg-white/10"}`}>{t}</span> : <span className="text-slate-600">—</span>;
const VEL_TONE = { Active: "text-emerald-300", Cooling: "text-amber-300", Lapsed: "text-orange-300", Dormant: "text-rose-300", New: "text-sky-300" };
export const Vel = ({ s }) => <span className={`text-[11.5px] font-medium ${VEL_TONE[s] || "text-slate-400"}`}>{s || "—"}</span>;
